import React from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAppContext } from '../../../contexts/AppContext';
import { useSubscriptionLock } from '../hooks/useSubscriptionLock'; // Importar hook
import SubscriptionBlocker from './SubscriptionBlocker';
import Spinner from '../../../components/ui/Spinner';
import { usePermissions } from '../../../hooks/usePermissions';
import { PARTNER_PAGES } from '../../../utils/routeUtils';

interface SubscriptionGuardProps {
    children: React.ReactNode;
    /**
     * Páginas que podem ser acessadas mesmo com bloqueio (ex: billing, subscription)
     */
    allowedPages?: string[];
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
    children,
    allowedPages = ['billing', 'subscription', 'profile']
}) => {
    // Usar hook centralizado de bloqueio
    const { isBlocked, status, daysUntilBlock, isLoading } = useSubscriptionLock();
    const { currentOrganization } = useOrganization();
    const { currentPage, setCurrentPage } = useAppContext();
    const { canViewBilling } = usePermissions();

    // Spinner só no boot (ainda sem org). Refresh em background mantém children montados
    // para não perder modais/forms abertos ao trocar de aba (TOKEN_REFRESHED).
    if (isLoading && !currentOrganization) {
        return (
            <div className="flex items-center justify-center h-full">
                <Spinner size="lg" />
            </div>
        );
    }

    const pagesWhenNoPlan = ['subscription', ...PARTNER_PAGES];
    const effectiveAllowed = status === 'no_plan' ? pagesWhenNoPlan : allowedPages;
    const isAllowedPage = effectiveAllowed.includes(currentPage);

    // Se bloqueado e não é página permitida, mostrar bloqueio
    // Se status for 'no_plan', o AuthenticatedLayout esconde o sidebar, e aqui forçamos o Blocker
    // Se o usuário já estiver na página 'subscription', isAllowedPage será true, então ele vê o conteúdo da página.
    if (isBlocked && !isAllowedPage && status) {
        return (
            <SubscriptionBlocker
                status={status}
                daysUntilBlock={daysUntilBlock}
                canManageBilling={canViewBilling}
                onNavigateToBilling={() => {
                    if (!canViewBilling) return;
                    setCurrentPage(
                        (status === 'trial_expired' || status === 'no_plan')
                            ? 'subscription'
                            : 'billing'
                    );
                }}
            />
        );
    }

    // Renderizar children normalmente
    return <>{children}</>;
};

export default SubscriptionGuard;
